import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Hand, Loader2, MessageSquareReply, UserPlus, X } from 'lucide-react';

import { Button } from '../../Common/button';
import { cn } from '../../../lib/utils';
import notify from '../../../lib/notify';
import { extractApiErrorMessage } from '../../../lib/apiError';
import projectService from '../../../services/projectService';
import type { ReviewRequest } from '../../../types/projects';
import { formatDateTime, timeAgo } from '../projectFormat';
import { personName } from './workflowFormat';

interface ReviewRequestsCardProps {
  requests: ReviewRequest[] | null;
  meId?: string;
  /** Owner of the project or qualifier: may ask and cancel any request. */
  canManage: boolean;
  onAsk?: () => void;
  onCancelled: (requestId: string) => void;
}

/** Answered (the recipient commented since being asked) or still pending. */
export const RequestStateBadge: React.FC<{ answered: boolean }> = ({ answered }) => {
  const { t } = useTranslation();
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-[11px] font-medium',
        answered
          ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
          : 'border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-400'
      )}
    >
      {answered ? t('projects.workflow.requests.answered') : t('projects.workflow.requests.pending')}
    </span>
  );
};

/** « Demandes d'avis »: who was asked, by whom, when, and whether they answered. */
export const ReviewRequestsCard: React.FC<ReviewRequestsCardProps> = ({
  requests,
  meId,
  canManage,
  onAsk,
  onCancelled,
}) => {
  const { t } = useTranslation();
  const [cancelling, setCancelling] = useState<string | null>(null);

  if (requests !== null && requests.length === 0 && !onAsk) return null;

  const cancel = async (r: ReviewRequest) => {
    setCancelling(r.id);
    try {
      await projectService.cancelReviewRequest(r.id);
      onCancelled(r.id);
      notify.info(t('projects.workflow.requests.cancelled'));
    } catch (error) {
      notify.error(extractApiErrorMessage(error, t('projects.workflow.requests.cancelError')));
    } finally {
      setCancelling(null);
    }
  };

  return (
    <section className="rounded-xl border border-border bg-card p-5">
      <div className="flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <Hand className="h-4 w-4 text-primary" aria-hidden />
          {t('projects.workflow.requests.title')}
          {!!requests?.length && (
            <span className="rounded-full border border-border px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
              {requests.length}
            </span>
          )}
        </h2>
        {onAsk && (
          <Button variant="outline" size="sm" className="h-7 gap-1.5 px-2 text-xs" onClick={onAsk}>
            <UserPlus className="h-3.5 w-3.5" />
            {t('projects.workflow.requests.ask')}
          </Button>
        )}
      </div>

      {requests === null ? (
        <p className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('projects.loading')}
        </p>
      ) : requests.length === 0 ? (
        <p className="mt-3 rounded-lg border border-dashed border-border px-4 py-5 text-center text-sm text-muted-foreground">
          {t('projects.workflow.requests.empty')}
        </p>
      ) : (
        <ul className="mt-2 divide-y divide-border">
          {requests.map((r) => {
            const mine = !!meId && r.requested_by?.id === meId;
            const canCancel = (mine || canManage) && r.to.id !== meId;
            const askedBy = personName(r.requested_by);
            return (
              <li key={r.id} className="py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="font-medium text-foreground">
                        {personName(r.to) || t('projects.workflow.requests.unknown')}
                      </span>
                      <RequestStateBadge answered={r.answered} />
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {askedBy && <>{t('projects.workflow.requests.askedBy', { name: askedBy })} · </>}
                      <span title={formatDateTime(r.created_at)}>{timeAgo(r.created_at)}</span>
                    </p>
                  </div>
                  {canCancel && (
                    <button
                      type="button"
                      onClick={() => void cancel(r)}
                      disabled={cancelling === r.id}
                      title={t('projects.workflow.requests.cancel')}
                      aria-label={t('projects.workflow.requests.cancel')}
                      className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-destructive disabled:opacity-50"
                    >
                      {cancelling === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
                    </button>
                  )}
                </div>
                {r.message && (
                  <p className="mt-1.5 flex gap-1.5 whitespace-pre-wrap break-words text-sm text-foreground">
                    <MessageSquareReply className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
                    <span>{r.message}</span>
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};
